import { ExternalTripCountsData, Settings, VehicleClass } from '../../data/api';
import styles from '../../styles/metrics/MetricsCharts.module.scss';
import { LinearUnitsInfo } from '../../utils/use-linear-units';
import { convertLinearUnitsForDisplay } from '../../utils/units-conversion';
import { ChartDataType } from './MetricChart';
import {
    formatAverageTripsPerDayMetadataMessage,
    formatTotalTripsMetadataMessage,
    getExternalCountForPeriod,
} from '../../utils/external-trip-counts';
import useTippy from '../../utils/use-tippy';
import { DataBucket } from './use-metrics';

interface HighlightedMetricProps {
    chartType: ChartDataType;
    value: number | null;
    unit: string;
    name: string;
    maximumFractionDigits?: number;
    externalTripCounts?: ExternalTripCountsData | null;
    citySettings?: Settings;
    dataBuckets?: DataBucket[] | null;
    metricType?: string;
    timeRange?: string;
    vehicleClass?: VehicleClass;
    totalCountForAvgCalc?: number;
}

function HighlightedMetric({
    chartType,
    value,
    unit,
    name,
    maximumFractionDigits = 0,
    externalTripCounts,
    citySettings,
    dataBuckets,
    metricType,
    timeRange,
    vehicleClass,
    totalCountForAvgCalc,
}: HighlightedMetricProps) {
    const asterisk =
        chartType === 'tripsPerDay' &&
        externalTripCounts &&
        getExternalCountForPeriod(
            timeRange,
            vehicleClass,
            externalTripCounts.tripCounts
        )
            ? '*'
            : '';
    const displayValue =
        value != null
            ? value.toLocaleString(undefined, { maximumFractionDigits }) +
              asterisk
            : '-' + asterisk;
    const displayUnit = value != null ? unit : null;
    let tooltipText: string | null = null;
    if (
        chartType === 'tripsPerDay' &&
        externalTripCounts &&
        getExternalCountForPeriod(
            timeRange,
            vehicleClass,
            externalTripCounts.tripCounts
        ) &&
        value &&
        timeRange &&
        vehicleClass &&
        dataBuckets &&
        citySettings
    ) {
        if (metricType === 'total') {
            tooltipText = formatTotalTripsMetadataMessage(
                value,
                timeRange,
                vehicleClass,
                externalTripCounts,
                citySettings.name,
                dataBuckets
            );
        }
        if (metricType === 'average' && totalCountForAvgCalc) {
            tooltipText = formatAverageTripsPerDayMetadataMessage(
                totalCountForAvgCalc,
                timeRange,
                vehicleClass,
                externalTripCounts,
                citySettings.name,
                dataBuckets
            );
        }
    }
    const [metricRef] = useTippy<HTMLDivElement>(tooltipText, {
        placement: 'bottom',
        allowHTML: true,
        interactive: true,
        appendTo: () => document.body,
    });

    return (
        <div ref={metricRef} className={styles.highlightedMetric}>
            <span className={styles.name}>{name}</span>
            <div className={styles.valueUnitContainer}>
                <span className={styles.value}>{displayValue}</span>
                <span className={styles.unit}>{displayUnit}</span>
            </div>
        </div>
    );
}

export function convertHighlightedMetricValuesForDisplay(
    chartType: ChartDataType,
    value: number | null,
    nativeUnits: string,
    linearUnitsForDisplay: LinearUnitsInfo
): number | null {
    if (value == null) {
        return null;
    }
    if (!chartType.includes('Distance')) {
        return value;
    }
    return convertLinearUnitsForDisplay(
        value,
        nativeUnits,
        linearUnitsForDisplay
    );
}

export function highlightedMetricDisplayUnits(
    units: string,
    linearUnits: LinearUnitsInfo
): string {
    // If units are a linear distance, show the user-selected linear units
    if (units === 'miles' || units === 'kilometers') {
        return linearUnits.units;
    }
    return units;
}

export default HighlightedMetric;
